import { passwordSchema } from "@eb/shared";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Button, ErrorBanner, Spinner } from "../components/ui";
import { authUrlError } from "../lib/supabase";
import { useAuth } from "../state/auth";
import { AuthShell, Field } from "./LoginPage";

/**
 * Step two of the password reset: the recovery link lands here.
 *
 * Opening the link signs the learner in -- `detectSessionInUrl` trades the fragment for a
 * session -- so "is there a user?" is exactly "was the link valid?". That is also why this
 * route sits outside `RedirectIfAuthed` in App.tsx: the recovery session would otherwise
 * bounce straight to the dashboard, and the learner would never get to type a new password.
 */
export function ResetPasswordPage() {
    const { user, loading, updatePassword } = useAuth();
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setError(null);

        const parsed = passwordSchema.safeParse(password);
        if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? "请检查密码");
            return;
        }
        if (parsed.data !== confirmation) {
            setError("两次输入的密码不一致");
            return;
        }

        setBusy(true);
        try {
            await updatePassword(parsed.data);
            setDone(true);
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "重置失败，请稍后重试");
        } finally {
            setBusy(false);
        }
    }

    if (loading) {
        return (
            <AuthShell title="重置密码" subtitle="正在检查链接">
                <Spinner label="正在验证重置链接" />
            </AuthShell>
        );
    }

    // No session means the fragment carried an error (expired, already used) or the page was
    // opened directly rather than from the mail.
    if (!user) {
        return (
            <AuthShell title="链接无效" subtitle="这个重置链接不能用了">
                <div className="space-y-4 text-sm text-ink-soft">
                    <ErrorBanner message={authUrlError ? "重置链接已失效或已经用过了" : "没有检测到有效的重置链接"} />
                    <p>重置链接一小时内有效，而且只能用一次。重新申请一封就好。</p>
                    <Link
                        to="/forgot-password"
                        className="block text-center font-medium text-brand hover:underline"
                    >
                        重新发送
                    </Link>
                </div>
            </AuthShell>
        );
    }

    if (done) {
        return (
            <AuthShell title="密码已更新" subtitle="可以继续复习了">
                <div className="space-y-4 text-sm text-ink-soft">
                    <p>新密码已经生效，这个账号已经登录好了。</p>
                    <Link to="/" className="block text-center font-medium text-brand hover:underline">
                        开始复习
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell title="设置新密码" subtitle={user.email ?? "重置密码"}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {error ? <ErrorBanner message={error} /> : null}

                <Field
                    label="新密码"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    required
                    placeholder="至少 8 个字符"
                />
                <Field
                    label="再输一次"
                    type="password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    autoComplete="new-password"
                    required
                />

                <Button type="submit" loading={busy} className="w-full">
                    保存新密码
                </Button>
            </form>
        </AuthShell>
    );
}
